import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';
import { type ColorScheme, getColorScheme } from '../utils/colorSchemes';

interface UserPreferences {
  color_scheme: ColorScheme;
  notifications_email: boolean;
  notifications_calendar: boolean;
  notifications_members: boolean;
}

interface UserPreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
  error: string | null;
  
  // Akcje
  loadPreferences: () => Promise<void>;
  updateColorScheme: (scheme: ColorScheme) => Promise<boolean>;
  updateNotificationSettings: (settings: Partial<Pick<UserPreferences, 'notifications_email' | 'notifications_calendar' | 'notifications_members'>>) => Promise<boolean>;
  getSchemeData: () => ReturnType<typeof getColorScheme>;
  clearCache: () => void;
}

const defaultPreferences: UserPreferences = {
  color_scheme: 'default',
  notifications_email: true,
  notifications_calendar: true,
  notifications_members: true
};

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set, get) => ({
      preferences: defaultPreferences,
      isLoading: false,
      error: null,

      loadPreferences: async () => {
        const { user } = useAuthStore.getState();
        if (!user) {
          console.log('🎨 Brak użytkownika, używam domyślnych preferencji');
          set({ preferences: defaultPreferences });
          return;
        }

        console.log('🎨 Ładowanie preferencji użytkownika...');
        set({ isLoading: true, error: null });

        try {
          // Sprawdź cache
          const cacheKey = `user-preferences-${user.id}`;
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            try {
              const parsed = JSON.parse(cached);
              if (Date.now() - parsed.timestamp < 5 * 60 * 1000) { // 5 min TTL
                console.log('📦 Używam cache dla preferencji użytkownika');
                set({ preferences: parsed.preferences, isLoading: false });
                return;
              }
            } catch (e) {
              // Ignore parsing errors
            }
          }

          const { data, error } = await supabase
            .from('user_preferences')
            .select('*')
            .eq('user_id', user.id)
            .single();

          if (error && error.code !== 'PGRST116') {
            throw error;
          }

          const preferences = data ? {
            color_scheme: (data.color_scheme as ColorScheme) || 'default',
            notifications_email: data.notifications_email ?? true,
            notifications_calendar: data.notifications_calendar ?? true,
            notifications_members: data.notifications_members ?? true
          } : defaultPreferences;

          // Cache wyniki
          sessionStorage.setItem(cacheKey, JSON.stringify({
            preferences,
            timestamp: Date.now()
          }));

          console.log('✅ Preferencje użytkownika załadowane');
          set({ preferences, isLoading: false });

        } catch (error: any) {
          console.error('❌ Błąd ładowania preferencji użytkownika:', error);
          set({ 
            error: error.message || 'Błąd ładowania preferencji',
            isLoading: false,
            preferences: defaultPreferences
          });
        }
      },

      updateColorScheme: async (scheme: ColorScheme) => {
        const { user } = useAuthStore.getState();
        if (!user) {
          console.error('❌ Brak użytkownika do aktualizacji schematu');
          return false;
        }

        console.log(`🎨 Zmiana schematu kolorystycznego użytkownika na: ${scheme}`);
        set({ isLoading: true, error: null });

        try {
          // Najpierw spróbuj zaktualizować istniejący rekord
          const { error: updateError } = await supabase
            .from('user_preferences')
            .update({
              color_scheme: scheme,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', user.id);
          
          // Jeśli nie ma rekordu do zaktualizowania, wstaw nowy
          if (updateError && updateError.code === 'PGRST116') {
            const { error: insertError } = await supabase
              .from('user_preferences')
              .insert({
                user_id: user.id,
                color_scheme: scheme,
                updated_at: new Date().toISOString()
              });
            
            if (insertError) throw insertError;
          } else if (updateError) {
            throw updateError;
          }

          console.log(`✅ Schemat kolorystyczny użytkownika zapisany: ${scheme}`);
          
          // Aktualizuj lokalny stan
          const currentPreferences = get().preferences;
          const newPreferences = { ...currentPreferences, color_scheme: scheme };
          set({ preferences: newPreferences, isLoading: false });
          
          // Wyczyść cache
          const cacheKey = `user-preferences-${user.id}`;
          sessionStorage.removeItem(cacheKey);
          
          return true;
        } catch (error: any) {
          console.error('❌ Błąd zapisywania schematu kolorystycznego:', error);
          set({ 
            error: error.message || 'Błąd zapisywania schematu kolorystycznego',
            isLoading: false 
          });
          return false;
        }
      },

      updateNotificationSettings: async (settings) => {
        const { user } = useAuthStore.getState();
        if (!user) {
          console.error('❌ Brak użytkownika do aktualizacji powiadomień');
          return false;
        }

        console.log('🔔 Aktualizacja ustawień powiadomień użytkownika...');
        set({ isLoading: true, error: null });

        try {
          const currentPreferences = get().preferences;
          const newNotificationSettings = { ...currentPreferences, ...settings };

          // Najpierw spróbuj zaktualizować istniejący rekord
          const { error: updateError } = await supabase
            .from('user_preferences')
            .update({
              notifications_email: newNotificationSettings.notifications_email,
              notifications_calendar: newNotificationSettings.notifications_calendar,
              notifications_members: newNotificationSettings.notifications_members,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', user.id);
          
          // Jeśli nie ma rekordu do zaktualizowania, wstaw nowy
          if (updateError && updateError.code === 'PGRST116') {
            const { error: insertError } = await supabase
              .from('user_preferences')
              .insert({
                user_id: user.id,
                notifications_email: newNotificationSettings.notifications_email,
                notifications_calendar: newNotificationSettings.notifications_calendar,
                notifications_members: newNotificationSettings.notifications_members,
                updated_at: new Date().toISOString()
              });
            
            if (insertError) throw insertError;
          } else if (updateError) {
            throw updateError;
          }

          console.log('✅ Ustawienia powiadomień użytkownika zapisane');
          
          set({ preferences: newNotificationSettings, isLoading: false });
          
          // Wyczyść cache
          const cacheKey = `user-preferences-${user.id}`;
          sessionStorage.removeItem(cacheKey);
          
          return true;
        } catch (error: any) {
          console.error('❌ Błąd zapisywania ustawień powiadomień:', error);
          set({ 
            error: error.message || 'Błąd zapisywania ustawień powiadomień',
            isLoading: false 
          });
          return false;
        }
      },

      getSchemeData: () => {
        const { preferences } = get();
        return getColorScheme(preferences.color_scheme);
      },

      clearCache: () => {
        console.log('🧹 Czyszczenie cache preferencji użytkownika');
        // Wyczyść sessionStorage dla wszystkich użytkowników (na wszelki wypadek)
        const keys = Object.keys(sessionStorage);
        keys.forEach(key => {
          if (key.startsWith('user-preferences-')) {
            sessionStorage.removeItem(key);
          }
        });
        // Resetuj stan do domyślnych preferencji
        set({ 
          preferences: defaultPreferences, 
          isLoading: false, 
          error: null 
        });
      }
    }),
    {
      name: 'user-preferences-storage',
      partialize: (state) => ({ preferences: state.preferences })
    }
  )
);